import React, { useMemo, useState } from 'react';
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Copy,
  Check,
  Info,
  XCircle,
  ScrollText,
} from 'lucide-react';
import type { ExtractionLogEntry, ExtractionLogSummary } from '../utils/extractionLogBuilder';
import { serializeExtractionLog } from '../utils/extractionLogBuilder';

interface ExtractionLogPanelProps {
  log: ExtractionLogSummary;
  defaultExpanded?: boolean;
}

type FilterMode = 'all' | 'error' | 'warning' | 'info';

const SEVERITY_STYLES: Record<
  ExtractionLogEntry['severity'],
  { icon: React.ElementType; row: string; badge: string; label: string }
> = {
  error: {
    icon: XCircle,
    row: 'border-red-800/50 bg-red-950/30',
    badge: 'bg-red-900/50 text-red-200',
    label: 'Error',
  },
  warning: {
    icon: AlertTriangle,
    row: 'border-amber-800/50 bg-amber-950/25',
    badge: 'bg-amber-900/50 text-amber-200',
    label: 'Advertencia',
  },
  info: {
    icon: Info,
    row: 'border-gray-700/60 bg-gray-800/40',
    badge: 'bg-gray-700/60 text-gray-300',
    label: 'Info',
  },
};

const ExtractionLogPanel: React.FC<ExtractionLogPanelProps> = ({
  log,
  defaultExpanded = false,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [filter, setFilter] = useState<FilterMode>('all');
  const [copied, setCopied] = useState(false);

  const filteredEntries = useMemo(() => {
    if (filter === 'all') return log.entries;
    return log.entries.filter(entry => entry.severity === filter);
  }, [log.entries, filter]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(serializeExtractionLog(log));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  const filterButtons: { id: FilterMode; label: string; count: number }[] = [
    { id: 'all', label: 'Todo', count: log.counts.total },
    { id: 'error', label: 'Errores', count: log.counts.errors },
    { id: 'warning', label: 'Advertencias', count: log.counts.warnings },
    { id: 'info', label: 'Info', count: log.counts.info },
  ];

  return (
    <div className="border border-gray-600 rounded-lg overflow-hidden">
      {/* Header */}
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-gray-800/80 hover:bg-gray-800 transition-colors text-left"
        aria-expanded={expanded}
      >
        <div className="flex items-center gap-2 min-w-0">
          <ScrollText className="w-4 h-4 text-blue-400 shrink-0" />
          <span className="text-sm font-semibold text-blue-300">Log de extracción</span>
          <div className="flex flex-wrap gap-1.5">
            {log.counts.errors > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-900/50 text-red-200">
                {log.counts.errors} error{log.counts.errors !== 1 ? 'es' : ''}
              </span>
            )}
            {log.counts.warnings > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-900/50 text-amber-200">
                {log.counts.warnings} adv.
              </span>
            )}
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-700/60 text-gray-400">
              {log.counts.total} entradas
            </span>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-gray-400 shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
        )}
      </button>

      {expanded && (
        <div className="px-4 py-3 space-y-3 bg-gray-900/40">
          {/* Meta + acciones */}
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
            <span>
              {new Date(log.processedAt).toLocaleString()} · {log.tableCount} tabla(s) ·{' '}
              {log.textLength.toLocaleString()} chars
            </span>
            <button
              type="button"
              onClick={handleCopy}
              className="inline-flex items-center gap-1 px-2 py-1 rounded border border-gray-600 text-gray-300 hover:bg-gray-800 transition-colors"
            >
              {copied ? (
                <>
                  <Check className="w-3 h-3 text-green-400" />
                  Copiado
                </>
              ) : (
                <>
                  <Copy className="w-3 h-3" />
                  Copiar JSON
                </>
              )}
            </button>
          </div>

          {/* Filtros */}
          <div className="flex flex-wrap gap-1.5">
            {filterButtons.map(btn => (
              <button
                key={btn.id}
                type="button"
                onClick={() => setFilter(btn.id)}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                  filter === btn.id
                    ? 'bg-blue-900/50 border-blue-600 text-blue-200'
                    : 'border-gray-600 text-gray-400 hover:border-gray-500'
                }`}
              >
                {btn.label} ({btn.count})
              </button>
            ))}
          </div>

          {/* Lista de entradas */}
          <div className="max-h-80 overflow-y-auto space-y-2 pr-1">
            {filteredEntries.length === 0 ? (
              <p className="text-sm text-gray-500 italic py-4 text-center">
                No hay entradas para este filtro.
              </p>
            ) : (
              filteredEntries.map(entry => {
                const style = SEVERITY_STYLES[entry.severity];
                const Icon = style.icon;
                return (
                  <div
                    key={entry.id}
                    className={`border rounded-md p-2.5 ${style.row}`}
                  >
                    <div className="flex items-start gap-2">
                      <Icon className="w-3.5 h-3.5 shrink-0 mt-0.5 opacity-80" />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${style.badge}`}>
                            {style.label}
                          </span>
                          <span className="text-[10px] text-gray-500">{entry.category}</span>
                          {entry.code && (
                            <span className="text-[10px] font-mono text-gray-600">
                              {entry.code}
                            </span>
                          )}
                          {entry.section && (
                            <span className="text-[10px] text-gray-600">· {entry.section}</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-200 leading-snug">{entry.message}</p>
                        {entry.detail && (
                          <p className="text-xs text-gray-400 mt-1 leading-snug">{entry.detail}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExtractionLogPanel;
